import { useState, useCallback, useMemo } from 'react'
import type { ReactNode } from 'react'
import { StakeholderService } from '../services/stakeholderService'
import { useFeatureAccessContext } from '../../../context/featureAccess'
import type { ProjectStakeholder, StakeholderTemplate } from '@bandos/shared'
import { StakeholderContext, type StakeholderContextType } from './StakeholderContextDef'

const DISABLED_RESULT = { success: false, error: 'Feature disabled' } as const

const DISABLED_CONTEXT: StakeholderContextType = {
  projectStakeholders: [],
  loading: false,
  error: null,
  reloadStakeholders: () => Promise.resolve(),
  addStakeholder: () => Promise.resolve(DISABLED_RESULT),
  removeStakeholder: () => Promise.resolve(DISABLED_RESULT),
  createTemplate: () => Promise.resolve(DISABLED_RESULT),
  updateTemplate: () => Promise.resolve(DISABLED_RESULT),
  deleteTemplate: () => Promise.resolve(DISABLED_RESULT),
}

export function StakeholderProvider({ children }: { children: ReactNode }) {
  const { isFeatureEnabled } = useFeatureAccessContext()

  if (!isFeatureEnabled('simulated_stakeholders')) {
    return (
      <StakeholderContext.Provider value={DISABLED_CONTEXT}>{children}</StakeholderContext.Provider>
    )
  }

  return <StakeholderProviderInner>{children}</StakeholderProviderInner>
}

function StakeholderProviderInner({ children }: { children: ReactNode }) {
  const [projectStakeholders, setProjectStakeholders] = useState<ProjectStakeholder[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Action: Reload stakeholders for a project
  const reloadStakeholders = useCallback(async (mindmapId: string) => {
    try {
      setLoading(true)
      setError(null)
      const response = await StakeholderService.getProjectStakeholders(mindmapId)

      if (response.error) {
        setError(response.error.message || 'Failed to load stakeholders')
        setProjectStakeholders([])
        return
      }
      setProjectStakeholders(response.data ?? [])
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load stakeholders'
      setError(errorMessage)
      setProjectStakeholders([])
    } finally {
      setLoading(false)
    }
  }, [])

  // Action: Add stakeholder to project (uses API response directly instead of reloading)
  const addStakeholder = useCallback(
    async (
      mindmapId: string,
      templateId: string
    ): Promise<{ success: boolean; error?: string; errorCode?: string }> => {
      try {
        setLoading(true)
        setError(null)
        const response = await StakeholderService.addStakeholderToProject(mindmapId, templateId)

        if (response.error) {
          const errorMessage = response.error.message || 'Failed to add stakeholder'
          const errorCode = response.error.code
          setError(errorMessage)
          return { success: false, error: errorMessage, errorCode }
        }

        // Add the new stakeholder directly to state (no reload needed)
        if (response.data) {
          const newStakeholder = response.data
          setProjectStakeholders((prev) => [...prev, newStakeholder])
        }
        return { success: true }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to add stakeholder'
        setError(errorMessage)

        return { success: false, error: errorMessage }
      } finally {
        setLoading(false)
      }
    },
    []
  )

  // Action: Remove stakeholder from project (optimistic update with rollback)
  const removeStakeholder = useCallback(
    async (
      mindmapId: string,
      stakeholderId: string
    ): Promise<{ success: boolean; error?: string }> => {
      // Store for potential rollback
      const previousStakeholders = projectStakeholders
      const stakeholderToRemove = projectStakeholders.find((s) => s.id === stakeholderId)

      if (!stakeholderToRemove) {
        return { success: false, error: 'Stakeholder not found' }
      }

      try {
        setError(null)

        // Optimistically remove from state immediately
        setProjectStakeholders((prev) => prev.filter((s) => s.id !== stakeholderId))

        const response = await StakeholderService.removeStakeholderFromProject(
          mindmapId,
          stakeholderId
        )

        if (response.error) {
          // Rollback on error
          setProjectStakeholders(previousStakeholders)
          const errorMessage = response.error.message || 'Failed to remove stakeholder'
          setError(errorMessage)
          return { success: false, error: errorMessage }
        }

        return { success: true }
      } catch (err) {
        // Rollback on exception
        setProjectStakeholders(previousStakeholders)
        const errorMessage = err instanceof Error ? err.message : 'Failed to remove stakeholder'
        setError(errorMessage)

        return { success: false, error: errorMessage }
      }
    },
    [projectStakeholders]
  )

  // Action: Create a new stakeholder template
  const createTemplate = useCallback(
    async (
      templateData: Omit<
        StakeholderTemplate,
        'created_at' | 'updated_at' | 'is_public' | 'created_by_user_id'
      > & { id: string }
    ): Promise<{
      success: boolean
      error?: string
      errorCode?: string
      template?: StakeholderTemplate
    }> => {
      try {
        setLoading(true)
        setError(null)
        const response = await StakeholderService.createTemplate(templateData)

        if (response.error) {
          const errorMessage = response.error.message || 'Failed to create template'
          const errorCode = response.error.code
          setError(errorMessage)
          return { success: false, error: errorMessage, errorCode }
        }

        return { success: true, template: response.data ?? undefined }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to create template'
        setError(errorMessage)

        return { success: false, error: errorMessage }
      } finally {
        setLoading(false)
      }
    },
    []
  )

  // Action: Update an existing stakeholder template
  const updateTemplate = useCallback(
    async (
      templateId: string,
      templateData: Partial<
        Omit<
          StakeholderTemplate,
          'id' | 'created_at' | 'updated_at' | 'is_public' | 'created_by_user_id'
        >
      >
    ): Promise<{ success: boolean; error?: string; template?: StakeholderTemplate }> => {
      try {
        setLoading(true)
        setError(null)
        const response = await StakeholderService.updateTemplate(templateId, templateData)

        if (response.error) {
          const errorMessage = response.error.message || 'Failed to update template'
          setError(errorMessage)
          return { success: false, error: errorMessage }
        }

        return { success: true, template: response.data ?? undefined }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to update template'
        setError(errorMessage)

        return { success: false, error: errorMessage }
      } finally {
        setLoading(false)
      }
    },
    []
  )

  // Action: Delete a stakeholder template
  const deleteTemplate = useCallback(
    async (templateId: string): Promise<{ success: boolean; error?: string }> => {
      try {
        setLoading(true)
        setError(null)
        const response = await StakeholderService.deleteTemplate(templateId)

        if (response.error) {
          const errorMessage = response.error.message || 'Failed to delete template'
          setError(errorMessage)
          return { success: false, error: errorMessage }
        }

        return { success: true }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to delete template'
        setError(errorMessage)

        return { success: false, error: errorMessage }
      } finally {
        setLoading(false)
      }
    },
    []
  )

  const value = useMemo<StakeholderContextType>(
    () => ({
      // State
      projectStakeholders,
      loading,
      error,

      // Actions
      reloadStakeholders,
      addStakeholder,
      removeStakeholder,
      createTemplate,
      updateTemplate,
      deleteTemplate,
    }),
    [
      projectStakeholders,
      loading,
      error,
      reloadStakeholders,
      addStakeholder,
      removeStakeholder,
      createTemplate,
      updateTemplate,
      deleteTemplate,
    ]
  )

  return <StakeholderContext.Provider value={value}>{children}</StakeholderContext.Provider>
}
